import { useState, useEffect, useContext } from 'react';
import { Grid, Box, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import {
    AppContext,
    SttLabelForm,
    SttFormText,
    SttButtonSave,
    SttButtonDelete,
    SttButtonRecover,
    SttButtonCancel,
    SttSelectGender,
} from '../../../sporttia/all';
import { SPORTCENTER_ACTIVITY_TEACHERS } from '../../../lib/queryKeys';
import { GENDER } from '../../../sporttia/selectors/SttSelectGender';
import DateBuilder from '../../../lib/DateBuilder';
import { getErrorMessage } from '../../../lib/utils';
import { PERMISSIONS } from '../../../lib/permissions';

type Teacher = {
    id?: number;
    name: string;
    login: string | null;
    surname1: string | null;
    surname2: string | null;
    dni: string | null;
    email: string | null;
    phone: string | null;
    gender: string | null;
    created?: string;
    trash?: boolean;
};

type Props = {
    idTeacher: string | number | null;
    onClose: () => void;
    onCreate: (teacherName: string, id: number) => void;
};

export default function TeacherDetail({
    idTeacher,
    onClose,
    onCreate,
}: Props): React.ReactElement {
    const queryClient = useQueryClient();
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const [creating, setCreating] = useState<boolean>(false);
    const [teacher, setTeacher] = useState<Teacher>();
    const [loading, setLoading] = useState<boolean>(false);

    /**
     * Load free schedule
     */
    const loadTeacher = (): void => {
        cxt?.api('GET', `/teachers/${idTeacher}`, {
            params: {
                trash: true,
            },
            success: (r: { teacher: Teacher }) => {
                setTeacher(r.teacher);

                // Set creating
                setCreating(false);
            },
        });
    };

    /**
     * Save free schedule
     */
    const saveTeacher = (): void => {
        setLoading(true);
        // Create
        if (creating) {
            cxt?.api('POST', `/scs/${cxt?.sc?.id}/teachers`, {
                params: {
                    ...teacher,
                    gender: teacher?.gender || GENDER.UNKNOWN,
                },
                success: (r: { teacher: Teacher }) => {
                    cxt?.showMessage('S', t('Created'));
                    setTeacher(r.teacher);
                    setCreating(false);
                    setLoading(false);
                    onCreate(r.teacher.name, r.teacher.id!);
                    queryClient.invalidateQueries({
                        queryKey: [SPORTCENTER_ACTIVITY_TEACHERS],
                    });
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });

            // Update
        } else {
            const putParameters = {
                name: teacher?.name,
                login: teacher?.login,
                surname1: teacher?.surname1,
                surname2: teacher?.surname2,
                email: teacher?.email,
                phone: teacher?.phone,
                gender: teacher?.gender || GENDER.UNKNOWN,
                dni: teacher?.dni,
            };
            cxt?.api('PUT', `/teachers/${teacher?.id}`, {
                params: putParameters,
                success: () => {
                    cxt?.showMessage('S', t('Saved'));
                    setLoading(false);
                },
                error: (err: any) => {
                    cxt.showMessage('E', getErrorMessage(err));
                    setLoading(false);
                },
            });
        }
    };

    /**
     * Delete free schedule
     */
    const delTeacher = (): void => {
        cxt?.api('DELETE', `/teachers/${teacher?.id}`, {
            confirmation: true,
            success: () => {
                cxt?.showMessage('S', t('Deleted'));
                queryClient.invalidateQueries({
                    queryKey: [SPORTCENTER_ACTIVITY_TEACHERS],
                });
                onClose();
            },
        });
    };

    /**
     * Recover free schedule
     */
    const recoverTeacher = (): void => {
        cxt?.api('PUT', `/teachers/${teacher?.id}/recover`, {
            confirmation: true,
            success: () => {
                cxt?.showMessage('S', t('Recovered'));
                loadTeacher();
                queryClient.invalidateQueries({
                    queryKey: [SPORTCENTER_ACTIVITY_TEACHERS],
                });
            },
        });
    };

    /**
     * Handle form
     */
    const handleForm = ({
        name,
        value,
    }: {
        name: string | undefined;
        value: unknown;
    }): void => {
        if (name) {
            setTeacher({ ...teacher!, [name]: value });
        }
    };

    /**
     * Init
     */
    useEffect(() => {
        if (idTeacher) {
            if (idTeacher === 'create') {
                // Init data to create
                setTeacher({
                    login: null,
                    name: '',
                    surname1: null,
                    surname2: null,
                    dni: null,
                    email: null,
                    phone: null,
                    gender: null,
                });

                // Set status
                setCreating(true);
            } else {
                loadTeacher();
            }
        }
    }, [idTeacher]);

    // -----| Render |-----
    return (
        <Box>
            {teacher && (
                <>
                    {!creating && (
                        <>
                            <Grid container spacing={3}>
                                <SttLabelForm
                                    grid
                                    md={2}
                                    title={t('Created')}
                                    text={new DateBuilder(
                                        teacher.created,
                                        cxt?.sc?.timezone?.name,
                                        cxt?.lang?.key,
                                    ).dmyhm()}
                                />
                            </Grid>

                            <Box my={4}>
                                <Divider />
                            </Box>
                        </>
                    )}

                    <Grid container spacing={3}>
                        <SttFormText
                            grid
                            md={3}
                            sm={12}
                            xs={12}
                            name="login"
                            caption={t('Login')}
                            defVal={teacher.login}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={9}
                            sm={12}
                            xs={12}
                            name="name"
                            caption={t('Name')}
                            defVal={teacher.name}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={6}
                            name="surname1"
                            caption={t('Surname1')}
                            defVal={teacher.surname1}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={6}
                            name="surname2"
                            caption={t('Surname2')}
                            defVal={teacher.surname2}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={4}
                            sm={12}
                            xs={12}
                            name="dni"
                            caption={t('DNI')}
                            defVal={teacher.dni}
                            onChange={handleForm}
                        />

                        <SttSelectGender
                            grid
                            md={4}
                            sm={12}
                            xs={12}
                            name="gender"
                            defVal={teacher.gender || GENDER.UNKNOWN}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={4}
                            sm={12}
                            xs={12}
                            name="phone"
                            caption={t('Phone')}
                            defVal={teacher.phone}
                            onChange={handleForm}
                        />

                        <SttFormText
                            grid
                            md={8}
                            sm={12}
                            xs={12}
                            name="email"
                            caption={t('Email')}
                            defVal={teacher.email}
                            onChange={handleForm}
                        />
                    </Grid>
                </>
            )}

            {teacher && (
                <Box className="mt-6 flex justify-start gap-4 flex-wrap">
                    {teacher.trash && (
                        <SttButtonRecover
                            onClick={() => recoverTeacher()}
                            permission={PERMISSIONS.ACTIVITY.MODIFY}
                        />
                    )}

                    {!creating && !teacher.trash && (
                        <SttButtonDelete
                            onClick={() => delTeacher()}
                            permission={PERMISSIONS.ACTIVITY.MODIFY}
                        />
                    )}

                    {!teacher.trash && (
                        <SttButtonSave
                            loading={loading}
                            onClick={() => saveTeacher()}
                            permission={PERMISSIONS.ACTIVITY.MODIFY}
                        />
                    )}

                    {creating && <SttButtonCancel onClick={() => onClose()} />}
                </Box>
            )}
        </Box>
    );
}
